import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccountBalance, ShoppingCart, SwapHoriz, Savings, HomeWork, CreditCard } from '@mui/icons-material';
import { formatCurrency } from '../utils/helpers';

const EXPLAINED_KEY = 'pinepocket.kindsExplained';

const readExplained = () => {
  try {
    return window.localStorage.getItem(EXPLAINED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Dashboard and Analytics: the money that left your bank accounts, and what it
 * did (spent, invested, big purchases, card bills, moved to your wallets and
 * other accounts). The parts add up to the total. The first time it appears, a
 * short note explains how this differs from spending.
 * @param {{ total, spent, invested, bigPurchases, cardBills, moved }} moneyOut
 */
const MoneyMovedCards = ({ moneyOut, sx }) => {
  const [explained, setExplained] = useState(readExplained);

  if (!moneyOut || !moneyOut.total) return null;

  const dismiss = () => {
    setExplained(true);
    try {
      window.localStorage.setItem(EXPLAINED_KEY, '1');
    } catch (e) {
      // Private mode: the note shows again next time
    }
  };

  const cards = [
    {
      title: 'Total outflow',
      value: moneyOut.total,
      icon: <AccountBalance color="primary" />,
      help: 'Everything that went out of your bank accounts: the five cards beside this add up to it. Moves between your own bank accounts aren\'t counted, since the money is still in your banks.',
      total: true,
    },
    {
      title: 'Spent from bank accounts',
      value: moneyOut.spent,
      icon: <ShoppingCart color="error" />,
      help: 'Spending paid straight from a bank account (UPI, debit card, transfers, EMIs). Card purchases are paid through the card bill.',
    },
    {
      title: 'Invested',
      value: moneyOut.invested,
      icon: <Savings color="success" />,
      help: 'SIPs, stocks, mutual funds and deposits. Saved, not spent, so it is not counted as spending.',
    },
    {
      title: 'Big purchases',
      value: moneyOut.bigPurchases,
      icon: <HomeWork color="secondary" />,
      help: 'A home, a car or other large things you own. Kept out of spending so one payment doesn\'t swamp the month, but it is money that left.',
    },
    {
      title: 'Card bills paid',
      value: moneyOut.cardBills,
      icon: <CreditCard color="action" />,
      help: 'Credit card bills paid from your bank accounts. The card purchases are in spending in the month you made them.',
    },
    {
      title: 'Moved to your accounts',
      value: moneyOut.moved,
      icon: <SwapHoriz color="action" />,
      help: 'Wallet top-ups and transfers to your own accounts outside the bank accounts here. Still your money.',
    },
  ];

  return (
    <Box sx={{ mb: 4, ...sx }}>
      {!explained && (
        <Alert severity="info" onClose={dismiss} sx={{ mb: 2 }}>
          Total outflow is all the money that went out of your bank accounts, split into what it did.
          Spending also counts card purchases when you make them, while here they count when you pay
          the card bill. Net Balance is income minus spending, investments and big purchases.
        </Alert>
      )}
      <Grid container spacing={3}>
        {cards.map(card => (
          <Grid size={{ xs: 12, sm: 6, md: 4, xl: 2 }} key={card.title}>
            <Tooltip title={card.help} placement="top">
              <Card variant="outlined" sx={{ height: '100%', ...(card.total && { borderColor: 'primary.main' }) }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {card.icon}
                    <Box>
                      <Typography variant="subtitle2" color={card.total ? 'primary' : 'text.secondary'}>{card.title}</Typography>
                      <Typography variant="h6" fontWeight="bold">{formatCurrency(card.value)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default MoneyMovedCards;
